import React, { useCallback, useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';

import type { EditorActions } from '@atlaskit/editor-core';
import type { ADNode } from '@atlaskit/editor-common/validator';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { TableOfContentsLoader } from '@confluence/table-of-contents';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features';
import { useSessionData } from '@confluence/session-data';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `${token('space.250')} ${token('space.250')}`,
	backgroundColor: token('color.background.neutral.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Centered = styled.div({
	display: 'flex',
	justifyContent: 'center',
});

type TableOfContentsLiveComponentProps = {
	macroDefaultProps: any;
	editorActions: EditorActions;
	subscribeDocChange: SubscriptionHandlerFnType;
	unsubscribeDocChange: SubscriptionHandlerFnType;
};

export const TableOfContentsLiveComponent = ({
	macroDefaultProps,
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
}: TableOfContentsLiveComponentProps) => {
	const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
	const [adf, setAdf] = useState<ADNode | undefined>();
	const { userId, cloudId } = useSessionData();
	const debounceMs = 400;

	const updateAdf = useCallback((editorView: EditorView | undefined) => {
		const doc = editorView?.state?.doc;

		if (doc) {
			const editorTransformer = new JSONTransformer();
			const _adf = editorTransformer.encode(doc);
			setAdf(_adf as ADNode);
		}
	}, []);

	useEffect(() => {
		const debouncedHandleDocChange = debounce(updateAdf, debounceMs);
		subscribeDocChange(debouncedHandleDocChange);

		return () => {
			unsubscribeDocChange(debouncedHandleDocChange);
		};
	}, [subscribeDocChange, unsubscribeDocChange, debounceMs, updateAdf]);

	useEffect(() => {
		// Ideally we would rely on the doc change handler to populate the ADF
		// but the doc change handler is sometimes not called on initial load.
		const editorView = editorActions._privateGetEditorView();
		updateAdf(editorView);
		setIsInitialLoadComplete(true);
	}, [editorActions, updateAdf]);

	if (!isInitialLoadComplete) {
		return (
			<Centered>
				<Spinner />
			</Centered>
		);
	} else if (!adf) {
		return <div />;
	}

	return (
		<Container>
			<TableOfContentsLoader
				isInEditMode
				userId={userId ?? ''}
				cloudId={cloudId ?? ''}
				adf={adf}
				macroDefaultProps={macroDefaultProps}
			/>
		</Container>
	);
};
